import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FormattedTextarea } from '@/components/ui/formatted-textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Sanitize function to remove HTML tags
const sanitizeText = (text: string) => {
  if (!text) return '';
  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .trim();
};

const formSchema = z.object({
  institution: z.string().min(1, { message: 'Institution is required' }),
  degree: z.string().min(1, { message: 'Degree is required' }),
  field_of_study: z.string().optional(),
  start_date: z.string().min(1, { message: 'Start date is required' }),
  end_date: z.string().optional(),
  current: z.boolean().default(false),
  location: z.string().optional(),
  description: z.string().optional().transform(sanitizeText),
});

type FormValues = z.infer<typeof formSchema>;

interface EducationFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  education?: any;
  onSuccess: () => void;
  onDelete?: () => void;
}

export default function EducationForm({ open, onOpenChange, education, onSuccess, onDelete }: EducationFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const isEditing = !!education?.id;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      institution: '',
      degree: '',
      field_of_study: '',
      start_date: '',
      end_date: '',
      current: false,
      location: '',
      description: '',
    },
  });

  // Reset form when education prop changes or dialog opens/closes
  useEffect(() => {
    if (open) {
      if (isEditing && education) {
        form.reset({
          institution: education.institution || '',
          degree: education.degree || '',
          field_of_study: education.field_of_study || '',
          start_date: education.start_date ? new Date(education.start_date).toISOString().split('T')[0] : '',
          end_date: education.end_date ? new Date(education.end_date).toISOString().split('T')[0] : '',
          current: education.current || false,
          location: education.location || '',
          description: education.description || '',
        });
      } else {
        form.reset({
          institution: '',
          degree: '',
          field_of_study: '',
          start_date: '',
          end_date: '',
          current: false,
          location: '',
          description: '',
        });
      }
    }
  }, [open, education, form, isEditing]);

  const onSubmit = async (data: FormValues) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const formattedData = {
        ...data,
        profile_id: user.id,
        end_date: data.current ? null : data.end_date,
        // Ensure description is sanitized
        description: sanitizeText(data.description || ''),
      };

      let error;
      
      if (isEditing) {
        const { error: updateError } = await supabase
          .from('education')
          .update(formattedData)
          .eq('id', education.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('education')
          .insert([formattedData]);
        error = insertError;
      }

      if (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.message || 'Failed to save education',
        });
      } else {
        toast({
          title: 'Success',
          description: `Education ${isEditing ? 'updated' : 'added'} successfully`,
        });
        onOpenChange(false);
        onSuccess();
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCurrentChange = (checked: boolean) => {
    if (checked) {
      form.setValue('end_date', '');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{isEditing ? 'Edit Education' : 'Add Education'}</DialogTitle>
            {isEditing && onDelete && (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={onDelete}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <DialogDescription>
            {isEditing ? 'Update your education details below.' : 'Add your education details below.'}
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="institution"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Institution</FormLabel>
                  <FormControl>
                    <Input placeholder="University of Example" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="degree"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Degree</FormLabel>
                  <FormControl>
                    <Input placeholder="Bachelor of Science" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="field_of_study"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Field of Study</FormLabel>
                  <FormControl>
                    <Input placeholder="Computer Science" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input 
                        type="date" 
                        {...field} 
                        disabled={form.watch('current')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="current"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        handleCurrentChange(checked as boolean);
                      }}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>I'm currently studying here</FormLabel>
                  </div>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Input placeholder="City, Country" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <FormattedTextarea
                      placeholder="Describe your studies, achievements, etc."
                      value={field.value || ''}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  </FormControl>
                  <FormDescription>
                    Include relevant coursework, achievements, or activities. Use plain text - formatting will be handled automatically.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditing ? 'Updating...' : 'Adding...'}
                  </>
                ) : (
                  isEditing ? 'Update Education' : 'Add Education'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}